import { InspirationRef, PlaceRef } from "@/types/entity"
import {
  DefaultInspirationPerPage,
  DetailedInspiration,
  DetailedInspirationItem,
  InspirationPayment,
  InspirationStatus,
  InspirationSuggestions,
  InspirationSummary,
  InspirationVersionsResponse,
} from "@/types/inspiration"
import { GeoCategory, PlaceSummary, PlaceType } from "@/types/place"
import uniqBy from "lodash/uniqBy"

import { PagingMeta } from "@/lib/paging"
import { baseApi } from "./base-api"

export const APP_HOME_GUIDES_LIMIT = 5

interface InspirationsListResponse {
  inspirations: InspirationSummary[]
}

export interface GetDetailedInspirationArgs {
  inspirationRef: InspirationRef
  batchSize?: number
}

export interface GetInspirationItemsArgs {
  inspirationRef: InspirationRef
  page: number
  perPage?: number
}

export interface GetInspirationsArgs {
  searchTerm?: string
  page?: number
  perPage?: number
  featured?: boolean
  embedded?: boolean
  isCrawler?: boolean // When true, skip merge for pagination (show single page)
}

export interface GetInspirationsResponse {
  data: InspirationSummary[]
  meta: PagingMeta
}

export interface GetInspirationsStaticListArgs {
  type: "home" | "featured"
  perPage?: number
}

export interface GetInspirationsStaticListResponse {
  inspirations: InspirationSummary[]
}

// For admin panel - more filtering options
export interface GetAdminInspirationsArgs {
  status?: InspirationStatus
  ownedOnly?: boolean
  payment?: InspirationPayment
  handle?: string
  searchTerm?: string
  page?: number
  perPage?: number
  sort?: string
}

// For creator hub - current user's inspirations
export interface GetUserInspirationsArgs {
  status?: InspirationStatus
  searchTerm?: string
  page?: number
  perPage?: number
}

export interface GetInspirationPlacesArgs {
  inspirationRef: InspirationRef
  placeType: PlaceType
  category?: GeoCategory
  page?: number
  perPage?: number
}

export interface GetInspirationPlacesResponse {
  data: PlaceSummary[]
  meta: PagingMeta
}

export const inspirationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInspirations: builder.query<
      GetInspirationsResponse,
      GetInspirationsArgs
    >({
      query: (args) => {
        const {
          searchTerm,
          page = 1,
          perPage = DefaultInspirationPerPage,
          featured,
          embedded = false,
        } = args
        const params = new URLSearchParams({
          type: "all",
          status: "published",
          owned_only: "false",
          page: page.toString(),
          per_page: perPage.toString(),
          payment: "free",
        })
        // Without search, we show 'featured' inspirations (unless embedded).
        // With search, we open up to all 'published' inspirations.
        const shouldShowFeatured = featured ?? (embedded ? false : !searchTerm)
        params.set("featured", shouldShowFeatured.toString())
        if (searchTerm) {
          params.set("search", searchTerm)
        }
        return {
          url: `/api/inspirations?${params.toString()}`,
        }
      },
      providesTags: ["Inspirations"],
      serializeQueryArgs: ({ queryArgs }) => {
        const perPage = queryArgs.perPage ?? DefaultInspirationPerPage
        // For crawlers, include page in cache key (no merging, single page view)
        // For users, exclude page to enable infinite scroll merge
        if (queryArgs.isCrawler) {
          return JSON.stringify({
            searchTerm: queryArgs.searchTerm || "",
            embedded: queryArgs.embedded,
            page: queryArgs.page || 1,
            perPage,
            isCrawler: true,
          })
        }
        return JSON.stringify({
          searchTerm: queryArgs.searchTerm || "",
          embedded: queryArgs.embedded,
          perPage,
        })
      },
      merge: (current, incoming, { arg }) => {
        // Crawlers don't merge - they view single pages
        if (arg.isCrawler) {
          return incoming
        }
        // Merge pagination results - append pages after page 1
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        // For page 1 (or no page specified): replace cache with fresh data
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getInspirationSuggestions: builder.query<
      InspirationSuggestions,
      { q: string; limit?: number }
    >({
      query: ({ q, limit }) => {
        const params = new URLSearchParams({ q })
        if (limit) params.append("limit", limit.toString())
        return { url: `/api/inspirations/suggest?${params}` }
      },
    }),
    // Unsigned-in homepage list — does not require a session
    getInspirationsStaticList: builder.query<
      InspirationSummary[],
      GetInspirationsStaticListArgs
    >({
      query: ({ type, perPage }) => {
        const params = new URLSearchParams({ type })
        if (perPage) {
          params.set("per_page", perPage.toString())
        }
        return {
          url: `/api/inspirations/by_ids?${params.toString()}`,
          skipAuthHandling: true,
        }
      },
      transformResponse: (response: GetInspirationsStaticListResponse) =>
        response.inspirations,
    }),
    getAppHomeGuides: builder.query<InspirationSummary[], PlaceRef>({
      queryFn: async (placeRef, _queryApi, _extraOptions, baseQuery) => {
        const placeResult = await baseQuery({
          url: `/api/inspirations/fetch/place/${placeRef}`,
          params: { limit: APP_HOME_GUIDES_LIMIT },
        })

        if (placeResult.error) {
          return { error: placeResult.error }
        }

        const locationGuides = (placeResult.data as InspirationsListResponse)
          .inspirations

        if (locationGuides.length >= APP_HOME_GUIDES_LIMIT) {
          return {
            data: locationGuides.slice(0, APP_HOME_GUIDES_LIMIT),
          }
        }

        const randomResult = await baseQuery({
          url: `/api/inspirations/fetch_random/${APP_HOME_GUIDES_LIMIT - locationGuides.length}`,
        })

        if (randomResult.error) {
          return { error: randomResult.error }
        }

        const randomGuides = (randomResult.data as InspirationsListResponse)
          .inspirations

        return {
          data: uniqBy(
            [...locationGuides, ...randomGuides],
            "entity_ref"
          ).slice(0, APP_HOME_GUIDES_LIMIT),
        }
      },
      keepUnusedDataFor: Infinity,
    }),
    // For admin panel - supports all filter options
    getAdminInspirations: builder.query<
      GetInspirationsResponse,
      GetAdminInspirationsArgs
    >({
      query: (args) => {
        const {
          status = "all",
          ownedOnly = false,
          payment,
          handle,
          searchTerm,
          page = 1,
          perPage = DefaultInspirationPerPage,
          sort,
        } = args
        const params = new URLSearchParams({
          status,
          owned_only: ownedOnly.toString(),
          page: page.toString(),
          per_page: perPage.toString(),
        })
        if (payment) params.set("payment", payment)
        if (handle) params.set("handle", handle)
        if (searchTerm) params.set("search", searchTerm)
        if (sort) params.set("sort", sort)
        return {
          url: `/api/inspirations?${params.toString()}`,
        }
      },
      providesTags: ["AdminInspirations"],
    }),
    // For creator hub - current user's inspirations
    getUserInspirations: builder.query<
      GetInspirationsResponse,
      GetUserInspirationsArgs
    >({
      query: (args) => {
        const {
          status = "all",
          searchTerm,
          page = 1,
          perPage = DefaultInspirationPerPage,
        } = args
        const params = new URLSearchParams({
          status,
          page: page.toString(),
          per_page: perPage.toString(),
        })
        if (searchTerm) params.set("search", searchTerm)
        return {
          url: `/api/inspirations/current-user?${params.toString()}`,
        }
      },
      providesTags: ["UserInspirations"],
    }),
    getDetailedInspiration: builder.query<
      DetailedInspiration,
      GetDetailedInspirationArgs
    >({
      query: ({ inspirationRef, batchSize }) => {
        let url = `/api/inspirations/${inspirationRef}`
        if (batchSize) {
          url += `?batch_size=${batchSize}`
        }

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      providesTags: (result, _error, { inspirationRef }) =>
        result ? [{ type: "DetailedInspiration", id: inspirationRef }] : [],
    }),
    getInspirationItems: builder.query<
      { data: DetailedInspirationItem[]; meta: { current_page: number } },
      GetInspirationItemsArgs
    >({
      query: ({ inspirationRef, page, perPage }) => {
        const params = new URLSearchParams()
        params.append("page", page.toString())
        if (perPage !== undefined) {
          params.append("per_page", perPage.toString())
        }
        const url = `/api/inspirations/${inspirationRef}/items?${params.toString()}`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      providesTags: (result, _error, { inspirationRef }) =>
        result ? [{ type: "InspirationItems", id: inspirationRef }] : [],
    }),
    getInspirationPlaces: builder.query<
      GetInspirationPlacesResponse,
      GetInspirationPlacesArgs
    >({
      query: (args) => {
        const {
          inspirationRef,
          placeType,
          category,
          page = 1,
          perPage = 20,
        } = args
        const params = new URLSearchParams({
          type: placeType,
          page: page.toString(),
          per_page: perPage.toString(),
        })
        if (placeType === "location" && category) {
          params.set("category", category)
        }
        return {
          url: `/api/inspirations/${inspirationRef}/places?${params.toString()}`,
        }
      },
      serializeQueryArgs: ({ queryArgs }) => {
        // Cache key excludes page for pagination merge
        return JSON.stringify({
          inspirationRef: queryArgs.inspirationRef,
          placeType: queryArgs.placeType,
          category: queryArgs.category,
        })
      },
      merge: (current, incoming, { arg }) => {
        // Merge pagination results - append pages after page 1
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        // For page 1 (or no page specified): replace cache with fresh data
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getInspirationVersions: builder.query<
      InspirationVersionsResponse,
      InspirationRef
    >({
      query: (inspirationRef) => ({
        url: `/api/inspirations/${inspirationRef}/versions`,
      }),
      keepUnusedDataFor: 0,
    }),
    restoreInspirationVersion: builder.mutation<
      InspirationSummary,
      { inspirationRef: InspirationRef; versionId: number }
    >({
      query: ({ inspirationRef, versionId }) => ({
        url: `/api/inspirations/${inspirationRef}/versions/${versionId}/restore`,
        method: "POST",
      }),
    }),
  }),
})

export const {
  useGetInspirationsQuery,
  useGetInspirationSuggestionsQuery,
  useGetInspirationsStaticListQuery,
  useGetAdminInspirationsQuery,
  useGetUserInspirationsQuery,
  useGetDetailedInspirationQuery,
  useLazyGetDetailedInspirationQuery,
  useGetInspirationItemsQuery,
  useLazyGetInspirationItemsQuery,
  useGetInspirationPlacesQuery,
  useLazyGetInspirationPlacesQuery,
  useGetAppHomeGuidesQuery,
  useGetInspirationVersionsQuery,
  useRestoreInspirationVersionMutation,
} = inspirationApi
